import React, {FunctionComponent, useEffect, useState} from 'react';
import {Autocomplete, Box, Button} from '@mui/material';
import TextField from '@mui/material/TextField';
import {
  CommandDTO,
  ModalForSendingTelecommand,
} from '../../../../../Shared/Components/ModalForSendingTelecommand';
import {findAllCommands} from '../../../../../Utils/CommandService';
import {useAppSelector} from '../../../../../Config/Hooks';
import {hasPermission} from '../../../../../Shared/Auth/AuthenticationUtil';
import {AUTHORITIES} from '../../../../../Config/Constants';

interface Props {
  equipmentId: number;
}

const InverterCommandSelector: FunctionComponent<Props> = ({equipmentId}) => {
  const account = useAppSelector((state) => state.authentication.account);
  const isAuthorizedToSendCommand = hasPermission(account?.authoritySet, [
    AUTHORITIES.SYSADMIN,
    AUTHORITIES.SEND_POWER_STATION_EQUIPMENT_DETAIL_COMMAND,
    AUTHORITIES.SEND_SKID_EQUIPMENT_DETAIL_COMMAND,
  ]);

  const [teleCommandList, setTeleCommandList] = useState<CommandDTO[]>([]);
  const [open, setOpen] = useState(false);
  const [commandDTO, setCommandDTO] = useState<CommandDTO | null>(null);

  useEffect(() => {
    setTeleCommandList([]);
    setCommandDTO(null);
    setOpen(false);

    if (equipmentId === -1) {
      return;
    }

    let cancelled = false;
    findAllCommands(equipmentId)
      .then((response: any) => {
        if (cancelled) {
          return;
        }
        // 204 é a resposta normalizada de "sem comandos cadastrados".
        if (response?.status === 200 || response?.status === 204) {
          setTeleCommandList(response.data?.data ?? []);
        } else {
          setTeleCommandList([]);
        }
      })
      .catch((error: any) => {
        if (!cancelled) {
          console.error('Error fetching tele commands:', error);
          setTeleCommandList([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [equipmentId]);

  const handleCommandChange = (_event: unknown, newValue: CommandDTO | null) => {
    setCommandDTO(newValue);
  };

  const handleSendButtonClick = () => {
    if (commandDTO == null || !isAuthorizedToSendCommand) {
      return;
    }
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setCommandDTO(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 2,
        gap: 1.5,
        alignItems: 'flex-end',
        width: '100%',
      }}
      data-testid="inverter-command-selector"
    >
      <Autocomplete
        id="inverter-detail-commands"
        aria-label="commands"
        value={commandDTO}
        fullWidth
        options={teleCommandList}
        getOptionLabel={(option) => option.teleObjectName}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        inputValue={commandDTO?.teleObjectName ?? ''}
        noOptionsText="Sem opções"
        renderInput={(params) => (
          <TextField {...params} label="Comandos" variant="standard" />
        )}
        onChange={handleCommandChange}
      />
      <Button
        disabled={commandDTO == null || !isAuthorizedToSendCommand}
        onClick={handleSendButtonClick}
        size="small"
        variant="contained"
        aria-label="Enviar comando"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '30px',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          minWidth: 72,
          textTransform: 'capitalize',
        }}
      >
        Enviar
      </Button>
      {commandDTO != null && (
        <ModalForSendingTelecommand
          commandDTO={commandDTO}
          open={open}
          onCloseModal={handleCloseModal}
        />
      )}
    </Box>
  );
};

export default InverterCommandSelector;
